export const FILTER_STATES = {
  NEUTRAL: null,
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
  ONLY: 'only'
};

/**
 * Determines tri-state filter state from URL params
 */
export const getTriStateValue = (urlParams, fieldName, value) => {
  const includeParam = urlParams[fieldName];
  const excludeParam = urlParams[`${fieldName}_not`];

  const includeValues = includeParam ? includeParam.split(',') : [];
  const excludeValues = excludeParam ? excludeParam.split(',') : [];

  if (excludeValues.includes(value)) return FILTER_STATES.EXCLUDE;
  if (includeValues.includes(value)) return FILTER_STATES.INCLUDE;
  return FILTER_STATES.NEUTRAL;
};

/**
 * Updates URL params based on tri-state change
 */
export const updateTriStateParams = (currentParams, fieldName, value, newState) => {
  const newParams = { ...currentParams, page: 1 };

  const includeKey = fieldName;
  const excludeKey = `${fieldName}_not`;

  const includeValues = newParams[includeKey] ? newParams[includeKey].split(',') : [];
  const excludeValues = newParams[excludeKey] ? newParams[excludeKey].split(',') : [];

  // Remove value from both arrays
  const filteredIncludes = includeValues.filter(v => v !== value);
  const filteredExcludes = excludeValues.filter(v => v !== value);

  // Add to appropriate array based on new state
  if (newState === FILTER_STATES.INCLUDE) {
    filteredIncludes.push(value);
  } else if (newState === FILTER_STATES.EXCLUDE) {
    filteredExcludes.push(value);
  }

  // Update params
  if (filteredIncludes.length > 0) {
    newParams[includeKey] = filteredIncludes.join(',');
  } else {
    delete newParams[includeKey];
  }

  if (filteredExcludes.length > 0) {
    newParams[excludeKey] = filteredExcludes.join(',');
  } else {
    delete newParams[excludeKey];
  }

  return newParams;
};

// List of all attribute query keys (needs to match ATTRIBUTE_FILTERS in AdminGroupList)
export const ATTRIBUTE_KEYS = [
  'anonymousContent.hasAccess',
  'externalAccess.hasAccess',
  'insights.hasAccess',
  'library.hasAccess',
  'memberContent.hasAccess',
  'moduleFeedback.hasAccess',
  'publicLandingPage.hasAccess',
  'isRegulated',
  'userRegistration.hasAccess'
];

/**
 * Get the attribute filter state from URL parameter value
 * @param {Object} urlParams - URL parameters
 * @param {string} queryKey - The attribute query key (e.g., 'library.hasAccess')
 * @returns {string} The filter state (NEUTRAL, INCLUDE, EXCLUDE, or ONLY)
 */
export const getAttributeFilterState = (urlParams, queryKey) => {
  const value = urlParams[queryKey];

  // If this attribute is set to 'only', return ONLY
  if (value === 'only') return FILTER_STATES.ONLY;

  // If ANY OTHER attribute is 'only', this one is excluded (for display purposes)
  const hasOtherOnlyAttribute = ATTRIBUTE_KEYS.some(
    key => key !== queryKey && urlParams[key] === 'only'
  );
  if (hasOtherOnlyAttribute) return FILTER_STATES.EXCLUDE;

  // Otherwise read the actual value
  if (value === 'include') return FILTER_STATES.INCLUDE;
  if (value === 'exclude') return FILTER_STATES.EXCLUDE;

  return FILTER_STATES.NEUTRAL;
};

/**
 * Update URL params for attribute filter state change
 * @param {Object} currentParams - Current URL parameters
 * @param {string} queryKey - The attribute query key (e.g., 'library.hasAccess')
 * @param {string} newState - New filter state (NEUTRAL, INCLUDE, EXCLUDE, or ONLY)
 * @returns {Object} Updated URL parameters
 */
export const updateAttributeFilterParams = (currentParams, queryKey, newState) => {
  const newParams = { ...currentParams, page: 1 };

  if (newState === FILTER_STATES.NEUTRAL) {
    // NEUTRAL: Remove the parameter
    delete newParams[queryKey];

  } else if (newState === FILTER_STATES.INCLUDE) {
    // INCLUDE: Set to 'include'
    newParams[queryKey] = 'include';

  } else if (newState === FILTER_STATES.EXCLUDE) {
    // EXCLUDE: Set to 'exclude'
    newParams[queryKey] = 'exclude';

  } else if (newState === FILTER_STATES.ONLY) {
    // ONLY: Set this attribute to 'only' and clear all other attribute filters
    newParams[queryKey] = 'only';

    // Clear all other attribute filters
    ATTRIBUTE_KEYS.forEach(attrKey => {
      if (attrKey !== queryKey) {
        delete newParams[attrKey];
      }
    });
  }

  return newParams;
};

/**
 * Handle setting attribute to ONLY state (for long-press)
 * @param {Object} currentParams - Current URL parameters
 * @param {string} queryKey - The attribute query key to set as ONLY
 * @returns {Object} Updated URL parameters
 */
export const setAttributeOnly = (currentParams, queryKey) => {
  const currentState = getAttributeFilterState(currentParams, queryKey);

  // If already ONLY, toggle back to NEUTRAL
  if (currentState === FILTER_STATES.ONLY) {
    return updateAttributeFilterParams(currentParams, queryKey, FILTER_STATES.NEUTRAL);
  }

  // Otherwise set to ONLY
  return updateAttributeFilterParams(currentParams, queryKey, FILTER_STATES.ONLY);
};
